// /src/store/modules/user.js
import cookie from 'vue-cookie'
import moment from 'moment' // Moment.js is not strictly necessary for this conversion but good to keep if used elsewhere.
import apiClient, { apiGet, apiPost, setJWTinAPI } from '@/services/API'
import { setJWTinAPIForms } from '@/services/FormAPIService'
import UserService from '@/services/UserService'
import { uStore } from '@/services/Storage'
import router from '@/router/router'

let userTimeout = null

export const namespaced = true

export const state = {
  user: null,
  token: null,
  groups: [],
  userExpiration: null,
  lastVisit: '',
}

export const mutations = {
  SET_USER_DATA(state, userData) {
    state.user = userData
  },
  SET_USER_TOKEN(state, userData) {
    state.token = userData == null ? userData : userData.token
  },
  SET_USER_EXPIRATION(state, expirationData) {
    // Ensure expirationData is always stored in milliseconds
    state.userExpiration = expirationData == null ? expirationData : expirationData
  },
  SET_USER_GROUPS(state, groups) {
    state.groups = groups
  },
}

export const actions = {
  login({ commit, dispatch }, credentials) {
    if (!credentials.username || !credentials.password) {
      dispatch(
        'notification/add',
        {
          type: 'error',
          msg: 'You must enter your username and password',
          closable: true,
          autoClose: true,
        },
        { root: true },
      )
      return false
    }

    return UserService.requestToken(credentials)
      .then(res => {
        if (res.status == 200 && typeof res.data.token !== 'undefined') {
          commit('SET_USER_TOKEN', res.data)
          if (res.data && res.data.hasOwnProperty('token_expiration')) {
            // Convert PHP seconds timestamp to JS milliseconds timestamp before committing
            commit('SET_USER_EXPIRATION', res.data.token_expiration * 1000) // <--- CHANGED HERE
          }

          // --- NEW: Re-initialize auth settings after successful user data update ---
          dispatch('initializeSessionAuth');
          // --- END NEW ---

          dispatch(
            'notification/add',
            {
              type: 'notice',
              msg: 'Welcome, ' + res.data.user_display_name,
              closable: true,
              autoClose: true,
            },
            { root: true },
          )
          return res.data
        } else if (res.request.status == 403) {
          const response = JSON.parse(res.request.response)
          dispatch(
            'notification/add',
            {
              type: 'error',
              msg: 'Login Attempt failed.',
              closable: true,
              autoClose: false,
            },
            { root: true },
          )
        } else {
          dispatch(
            'notification/add',
            {
              type: 'error',
              msg: `ERROR ${res.request.status}: ${res.request.statusText} `,
              closable: true,
              autoClose: false,
            },
            { root: true },
          )
        }
      })
      .catch(err => {
        dispatch(
          'notification/add',
          {
            type: 'error',
            msg: 'There was a problem logging in: ' + err.response,
            closable: true,
            autoClose: false,
          },
          { root: true },
        )
      })
    return true
  },
  async requestUserData({ commit, state, dispatch }, post = {}) {
    try {
      const { token } = state
      if (token) setJWTinAPI(token)
      if (token) setJWTinAPIForms(token)
      const res = await apiPost('users/me', post)

            // Explicitly check for 401 Unauthorized (session timed out)
      if (res && res.status === 401) {
        console.warn('API Response 401 Unauthorized in requestUserData. Logging out...')
        dispatch('logout')
        dispatch(
          'notification/add',
          {
            type: 'Notice',
            msg: `Your session has timed out. Please log back in.`,
            closable: false,
            autoClose: true,
          },
          { root: true },
        )
        return false; // Stop further processing for this failed request
      }
        
      // Explicitly check for 403 Forbidden (permission denied)
      if (res && res.status === 403) {
        console.warn('API Response 403 Forbidden in requestUserData. Logging out...')
        dispatch('logout')
        dispatch(
          'notification/add',
          {
            type: 'Error', // Using 'Error' as it aligns with your login's 403 notification type
            msg: `Access denied. Your session might be invalid or you lack the necessary permissions. Please log back in.`,
            closable: true,
            autoClose: true,
          },
          { root: true },
        )
        return false; // Stop further processing for this failed request
      }

      if (res && res.status == 200) {
        commit('SET_USER_DATA', res.data)
        if (res.data && res.data.hasOwnProperty('acf') && res.data.acf.hasOwnProperty('user_groups')) commit('SET_USER_GROUPS', res.data.acf.user_groups)
        // Convert PHP seconds timestamp to JS milliseconds timestamp before committing
        commit('SET_USER_EXPIRATION', res.data.token_expiration[0] * 1000)

        // --- NEW: Re-initialize auth settings after successful user data update ---
        dispatch('initializeSessionAuth');
        // --- END NEW ---
        return res.data
      } else if (res && res.status === 0) {
        // Network failure or server unreachable — don't log out, just fail silently.
        // The next putInteraction retry (every 5s during playback) will pick it up.
        console.warn('requestUserData: network failure or server unreachable')
        return false
      } else {
        if (res && res.request && currentURLObj.hostname == 'localhost' && res.request.status !== 503)
          dispatch(
            'notification/add',
            {
              type: 'Error',
              msg: `Whoops, there was an issue updating your account: ERROR ${res.request.status}, ${res.request.statusText}`,
              closable: true,
              autoClose: true,
            },
            { root: true },
          )
      }
    } catch (err) {
      // Transient server errors (5xx) — don't logout, let next retry handle it
      if (err.response && err.response.status >= 500) {
        console.warn('requestUserData: transient server error', err.response.status)
        return false
      }
      console.warn(err)
      dispatch(
        'notification/add',
        {
          type: 'Error',
          msg: `A critical error occurred while communicating with the server: ${err.message}. Please try again, or log in if the problem persists.`,
          closable: true,
          autoClose: true,
        },
        { root: true },
      )
      dispatch('logout')
      return false;
    }
  },

  tokenValidation({ state, dispatch }) {
    const { token } = state

    return UserService.validateJWT(token)
      .then(res => {
        return true
      })
      .catch(err => {
        dispatch('logout')
        dispatch(
          'notification/add',
          {
            type: 'Notice',
            // User might be undefined if logout cleared state immediately,
            // so use a generic message here if user data isn't guaranteed.
            // Or ensure user.first_name is safely accessed.
            msg: `Unfortunately your session has timed out, please log back in.`, // Simplified for safety
            closable: false,
            autoClose: true,
          },
          { root: true },
        )
        console.warn(err)
        return false
      })
  },

  // This new action handles setting up JWT for API and cookie
  initializeSessionAuth({ state }) {
    const { token, userExpiration } = state;
    if (token !== null) {
      const isHttps = window.location.protocol === 'https:';
      setJWTinAPI(token);
      setJWTinAPIForms(token);
      const expires = new Date(userExpiration); // userExpiration is now in milliseconds
      if (cookie.get('ac_session') != token) {
        cookie.set('ac_session', token, { expires, sameSite: 'Strict', secure: isHttps });
      }
    }
  },

    // --- NEW ACTION START: Reauthenticate user from a stored token (e.g., from cookie) ---
  async reauthenticateFromCookie({ commit, dispatch }, storedToken) {
    if (!storedToken) {
      console.log('reauthenticateFromCookie: No token provided.');
      return false; // Indicate failure to reauthenticate
    }

    try {
      // Temporarily set the token in state and initialize API headers.
      // This is crucial for `tokenValidation` to have the token available.
      commit('SET_USER_TOKEN', { token: storedToken });
      dispatch('initializeSessionAuth'); // Sets global axios header and cookie

      // Validate the token with the backend.
      // This will use UserService.validateJWT and handle 401/403 errors by dispatching `logout`.
      const isValid = await dispatch('tokenValidation');

      if (isValid) {
        // If token is valid, fetch full user data.
        // `requestUserData` will handle setting `SET_USER_DATA` and `SET_USER_EXPIRATION`.
        const userData = await dispatch('requestUserData', {}); // Pass empty object, just need to fetch user data

        if (userData) {
          // console.log('reauthenticateFromCookie: User reauthenticated successfully.');
          return true; // Indicate success
        }
      }
      // If validation or user data fetch failed (e.g., isValid is false, or userData is null/false)
      console.warn('reauthenticateFromCookie: Token validation or user data fetch failed, clearing user data.');
      dispatch('clearUserData'); // Ensure all user data is cleared if reauthentication fails
      return false; // Indicate failure
    } catch (error) {
      // Catch any unexpected errors during the reauthentication process
      console.error('reauthenticateFromCookie: An unexpected error occurred during reauthentication:', error);
      dispatch('clearUserData'); // Always clear data on critical errors
      return false; // Indicate failure
    }
  },
  
  expire({ state, dispatch }) {
    const { user, token, userExpiration } = state // <--- Access userExpiration from state directly
    if (token === null) return false
    if (token !== null && userTimeout === null) {
      const tokenExp = userExpiration // <--- NOW tokenExp is already in milliseconds
      var currentTime = Date.now() // currentTime is in milliseconds
      // currentTime is being set within function.php to make sure it is server
      const timeOut = currentTime < tokenExp ? (tokenExp - currentTime) : 0 // <--- REMOVED * 1000 HERE

      userTimeout = setTimeout(function() {
        dispatch('tokenValidation')
      }, timeOut)
      return true
    }
  },
    
  putInteraction({ commit, state, dispatch }, post = {}) {
    // REMOVED per-interaction tokenValidation — was hammering the server every 5 seconds
    // during media playback, causing connection starvation and forced logouts on audio.
    // Auth is now enforced via:
    //   1. Token expiration timer (expire action) — validates once at timeout
    //   2. requestUserData handles 401/403 responses by dispatching logout
    const { type, id, visited } = post
    if (typeof type == 'undefined' || typeof id == 'undefined') {
      return false
    }

    // Quick client-side token check — no API call, just checks if we have one
    if (!state.token) {
      dispatch('logout')
      return false
    }

    return dispatch('requestUserData', post)
      .then(res => {
        if (!res) return false // requestUserData returned false (401/403/timeout)

        // Update lastVisited — only once per day per session to reduce API calls
        if (visited && state.user && state.user.lastVisited && state.user.lastVisited[0]) {
          const currentVisited = new Date(state.user.lastVisited[0]).setHours(23,59,59,0)
          const newVisited = new Date(visited).getTime()

          if (newVisited > currentVisited)  {
            const lastVisitedObj = {
              type: 'lastVisited',
              stringValue: visited,
            }
            return dispatch('requestUserData', lastVisitedObj)
              .then(res => res)
              .catch(err => err)
          }
        }
        return res
      })
      .catch(err => {
        console.error('Error in putInteraction:', err);
        return err;
      });
  },
  
  logout({ state, commit, dispatch }) {
    const { user } = state
    dispatch(
      'notification/add',
      {
        type: 'Notice',
        msg: `Logging you out! See you next time ${user ? user.first_name : 'friend'}.`, // Safely access user.first_name
        closable: false,
        autoClose: true,
      },
      { root: true },
    )
    dispatch('clearUserData')
    window.location.href = '/sign-in'
  },
    
  clearUserData({ commit }) {
    commit('SET_USER_DATA', null)
    commit('SET_USER_TOKEN', null)
    commit('SET_USER_EXPIRATION', null)
    commit('SET_USER_GROUPS', [])
    cookie.delete('ac_session')
    localStorage.clear()
  },
}

export const getters = {
  getUser: state => {
    const { user } = state
    return user ? user : false
  },

  getAllInteractions: (state, getters) => {
    const output = []
    const user = getters['getUser'];
    // Safely access properties as user might be false/null
    if (!user) return [];
    const { session, course, relationships } = user
    const allInteractions = [session, course, relationships, user['bible-studies']]
    let i = 0
    do {
      if (
        typeof allInteractions[i] != 'undefined' &&
        typeof allInteractions[i][0] == 'object'
      )
        output.push(Object.values(allInteractions[i][0]))
      i++
    } while (i < allInteractions.length)
    return output.flat()
  },

  findInteractionsByProp: (state, getters) => (array = [0], propKey = 'id') => {
    const { getAllInteractions } = getters
    const collection = []
    let i = 0
    do {
      const item = getAllInteractions.find(item => item[propKey] == array[i])
      if (typeof item != 'undefined') {
        collection.push(item)
      } else {
        let output = {}
        output[propKey] = array[i]
        collection.push(output)
      }
      i++
    } while (i < array.length)
    return collection
  },

  getCompleted: (state, getters) => {
    const { getAllInteractions } = getters
    return getAllInteractions.filter(item => {
      return !(
        typeof item.dateCompleted == 'undefined' || item.dateCompleted == 'null'
      )
    })
  },

  getBookmarks: (state, getters) => {
    const { getAllInteractions } = getters
    return getAllInteractions.filter(item => {
      return (!(typeof item.bookmarked == 'undefined' || item.bookmarked == false))
    })
  },

  getBookmarksOfType: (state, getters) => (type = '') => {
    const { getAllInteractions } = getters
    return getAllInteractions.filter(item => {
      return (!(typeof item.bookmarked == 'undefined' || item.bookmarked == false) && item.type == type)
    })
  },

  getStartDate: state => {
    const { user } = state
    return (user && user.acf && user.acf.start_date) ? user.acf.start_date : false
  },

  getInteraction: state => (type, id) => {
    if (typeof type == 'undefined' || typeof id == 'undefined') {
      return false
    }
    let { user } = state
    // Ensure user object is not null/undefined before spreading or accessing properties
    if (!user) return false;
    user = { ...user } // Spreading user is generally not needed if just reading properties
    return user[type] &&
      user[type][0] &&
      typeof user[type][0][id] != 'undefined'
      ? user[type][0][id]
      : false
  },

  getCustomFields: state => async () => {
    try {
      const { user } = state // await is not needed here
      return (user && user.acf) ? user.acf : false
    } catch (err) {
      console.log(err)
      return false; // Return false on error
    }
  },

  getUserGroups: state => async () => {
    try {
      const { groups } = state // await is not needed here
      // console.log(groups)
      return groups ? groups : false
    } catch (err) {
      console.log(err)
      return false; // Return false on error
    }
  },

  authCheck: state => {
    return state.token !== null
  },
}